// src/components/EntryList.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Grid
} from "@mui/material";
import api,
  { fetchEntries,
  saveEntries,
  deleteEntry,
  importBlotter,
  fetchCounterparties } from "../api";
import CreateEntryModal from "./CreateEntryModal";
import {
  ConfirmationDialog,
  PamGrid,
  useNotification,
  ValidationErrorModal,
  nullToEmpty,
  HistoryModal,
  parseFileData,
  buildHeaderMappingsFromAliases,
  createColumnNormalizer,
  normalizeRowKeys,
  mergeRows,
  createChoicesValidator,
  combineValidators,
  GridConfigMenu,
  useGridConfigs,
  notifyPermissionError,
  getWriteErrorMessage,
  isVersionConflict,
  loadedModifiedDate,
  shouldReloadAfterWriteError,
  MODIFIED_DATE_FIELD,
} from '@1823-partners/core';
import EntryHistory from "./EntryHistory";
import { formatDate } from "../utils";
import { isValueNull, formatEntryData, validateEntryList, formatValidationErrors, parseNumericCellValue, isPlaceholderEntryId, newEntryId } from "../utils/entryUtils";
import config from "../data/config.json";
import { getOwnerFieldOptions, getAcceptedOwnerValues } from '../utils/ownerChoices';

// Header alias map for file uploads — maps display names to database column names
const ENTRY_HEADER_ALIASES = {
  BLOTTER_ID: ['Blotter ID'],
  DIRECTION: ['Direction'],
  DATE: ['Trade Date'],
  CUSIP: ['CUSIP'],
  CURRENCY: ['Currency'],
  SEC_OWNER: ['Security Owner'],
  SEC_OWNER_TEAM: ['Security Owner Team'],
  TYPE: ['Type'],
  COUNTERPARTY: ['Counterparty'],
  CONNECTED_ENTITY: ['Connected Entity'],
  ILLIQUID: ['Illiquid (Y/N)'],
  FLOATER: ['Floater (Y/N)'],
  DESCRIPTION: ['Description'],
  ORIGINAL_FACE: ['Original Face'],
  CURRENT_FACE: ['Current Face'],
  PRICE: ['Price'],
  MARKET_SPREAD: ['Market Convention Spread'],
  TREAS_SPREAD: ['Treasury Equivalent Spread'],
  BASE_YIELD: ['Base Case Yield'],
  WAL: ['WAL'],
  DURATION: ['Duration'],
  OVERRIDE: ['Override (Y/N)'],
  RATING: ['Rating at Trade'],
  CURR_CREDIT_SUPPORT: ['Current Credit Support'],
  SSFA_DETACHMENT_POINT: ['SSFA Detachment Point'],
  RISK_RETENTION: ['Risk Retention'],
  ACCOUNT: ['Account'],
  NOTES: ['Notes'],
};

const ENTRY_HEADER_MAPPINGS = buildHeaderMappingsFromAliases(ENTRY_HEADER_ALIASES);
const entryColumnNormalizer = createColumnNormalizer(ENTRY_HEADER_MAPPINGS);

// Dropdown field choices for upload validation — values must match allowed dropdown options
const DROPDOWN_FIELD_CHOICES = {
  DIRECTION: config.directions,
  CURRENCY: config.currencies,
  SEC_OWNER: getAcceptedOwnerValues('SEC_OWNER'),
  SEC_OWNER_TEAM: getAcceptedOwnerValues('SEC_OWNER_TEAM'),
  TYPE: config.securityTypes,
  CONNECTED_ENTITY: config.connectedEntities,
  ILLIQUID: config.booleanOptions,
  FLOATER: config.booleanOptions,
  OVERRIDE: config.booleanOptions,
  RATING: config.ratings,
  RISK_RETENTION: config.riskRetentionOptions,
  ACCOUNT: config.accounts,
};

const validateDropdownValues = combineValidators(
  ...Object.entries(DROPDOWN_FIELD_CHOICES).map(
    ([field, choices]) => createChoicesValidator(field, choices)
  )
);

/**
 * Shared post-processing for entry rows parsed from file uploads.
 * Handles numeric field parsing, date formatting, boolean normalization,
 * and calculated fields (EST_PROCEEDS, THICKNESS).
 */
const processEntryRow = (entry, userId) => {
  // Set default values
  entry.MODIFIED_BY = entry.MODIFIED_BY || userId;
  // Every uploaded row is a new key: its token is null, never the file's value.
  entry.MODIFIED_DATE = null;
  entry.CURRENCY = entry.CURRENCY || "USD";

  // Ensure CUSIP is always a string
  if (entry.CUSIP !== undefined && entry.CUSIP !== null && entry.CUSIP !== '') {
    entry.CUSIP = entry.CUSIP.toString();
  }

  // Convert numeric fields to proper numbers
  const numericFields = [
    'ORIGINAL_FACE', 'CURRENT_FACE', 'PRICE', 'MARKET_SPREAD',
    'TREAS_SPREAD', 'BASE_YIELD', 'WAL', 'DURATION',
    'CURR_CREDIT_SUPPORT', 'SSFA_DETACHMENT_POINT'
  ];

  numericFields.forEach(field => {
    if (entry[field] && entry[field] !== '') {
      try {
        let cleanValue = entry[field].toString().replace(/,/g, '');
        if (field === 'WAL' && cleanValue.toLowerCase().endsWith('yr')) {
          cleanValue = cleanValue.slice(0, -2).trim();
        }
        if (field === 'BASE_YIELD' && cleanValue.includes('%')) {
          cleanValue = cleanValue.replace(/%/g, '') / 100;
        }
        const numValue = Number(cleanValue);
        if (!isNaN(numValue) && isFinite(numValue)) {
          entry[field] = numValue;
        } else {
          entry[field] = null;
        }
      } catch {
        entry[field] = null;
      }
    } else {
      entry[field] = entry[field] === '' ? null : entry[field];
    }
  });

  // Format date field — handle Excel dates and treat invalid dates as null
  if (entry.DATE) {
    try {
      let dateValue = entry.DATE;
      if (typeof dateValue === 'number' && dateValue > 1 && dateValue < 100000) {
        const excelEpoch = new Date(1899, 11, 30);
        const jsDate = new Date(excelEpoch.getTime() + (dateValue * 24 * 60 * 60 * 1000));
        dateValue = jsDate;
      }
      const formattedDate = formatDate(dateValue);
      if (formattedDate && formattedDate !== 'Invalid Date') {
        entry.DATE = formattedDate;
      } else {
        entry.DATE = null;
      }
    } catch {
      entry.DATE = null;
    }
  }

  // Convert boolean fields from various formats to Y/N
  const booleanFields = ['ILLIQUID', 'FLOATER', 'OVERRIDE'];
  booleanFields.forEach(field => {
    if (entry[field]) {
      try {
        const value = entry[field].toString().toLowerCase().trim();
        if (['true', '1', 'yes', 'y'].includes(value)) {
          entry[field] = 'Y';
        } else if (['false', '0', 'no', 'n'].includes(value)) {
          entry[field] = 'N';
        } else {
          entry[field] = null;
        }
      } catch {
        entry[field] = null;
      }
    }
  });

  // Calculate EST_PROCEEDS
  const currentFace = entry.CURRENT_FACE;
  const price = entry.PRICE;
  const direction = entry.DIRECTION;
  if (!isValueNull(price) && !isValueNull(currentFace) && !isValueNull(direction)) {
    const multiplier = direction.toUpperCase() === 'BUY' ? 1 : -1;
    entry.EST_PROCEEDS = (price * currentFace / 100) * multiplier;
  } else {
    entry.EST_PROCEEDS = null;
  }

  // Calculate THICKNESS
  const currCreditSupport = entry.CURR_CREDIT_SUPPORT;
  const ssfaDetachmentPoint = entry.SSFA_DETACHMENT_POINT;
  if (!isValueNull(currCreditSupport) && !isValueNull(ssfaDetachmentPoint)) {
    entry.THICKNESS = ssfaDetachmentPoint - currCreditSupport;
  } else {
    entry.THICKNESS = null;
  }

  return entry;
};

// Grid cell values compare as text: loaded nulls are '' (nullToEmpty) and
// numeric editors hand back numbers.
const sameCellValue = (a, b) => String(a ?? '') === String(b ?? '');

const EntryList = ({ userId }) => {
  const [editingEntry, setEditingEntry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEntryForHistory, setSelectedEntryForHistory] = useState(null);
  const [newRowCount, setNewRowCount] = useState(0);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [counterparties, setCounterparties] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [validationErrorModalOpen, setValidationErrorModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [updatedRows, setUpdatedRows] = useState({}); // Dictionary to track rows with updated cells
  const [analyticsType, setAnalyticsType] = useState('sum');
  const [analyticsCurrency, setAnalyticsCurrency] = useState('USD'); // Default to USD
  const [filtersChanged, setFiltersChanged] = useState(0); // Counter to trigger analytics updates on filter changes
  const [analyticsRefreshTrigger, setAnalyticsRefreshTrigger] = useState(0); // General trigger for analytics refresh
  const theme = useTheme();
  const importFileInputRef = useRef(null);
  const gridRef = useRef(null);
  const isPastingRef = useRef(false);
  const pendingUpdatedRowsRef = useRef(null);
  const updatedRowsRef = useRef(updatedRows);
  updatedRowsRef.current = updatedRows;
  // new_row_<n> placeholder -> the BLOTTER_ID its save sends. Picked once and
  // kept until the row is saved, so a retry after an unknown outcome sends the
  // same key (the writer 409s it if the first attempt landed).
  const newRowIdsRef = useRef({});
  // The BLOTTER_ID a placeholder's save sends, picked on first use. Grid saves
  // and the edit modal share it, so no path can land the row under two ids.
  const reserveEntryId = useCallback((placeholder) => {
    if (!newRowIdsRef.current[placeholder]) {
      newRowIdsRef.current[placeholder] = newEntryId();
    }
    return newRowIdsRef.current[placeholder];
  }, []);
  const showAnalytics = String(process.env.REACT_APP_SHOW_BLOTTER_ANALYTICS || '').toLowerCase() === 'true';

  const { showNotification } = useNotification();

  // Fetch counterparties on component mount
  useEffect(() => {
    const loadCounterparties = async () => {
      try {
        const response = await fetchCounterparties();
        // Extract only the COUNTERPARTY field from each object
        const counterpartyList = (response.data || []).map(item => item.COUNTERPARTY).filter(cp => cp);
        setCounterparties(counterpartyList);
      } catch (error) {
        console.error('Error fetching counterparties:', error);
        notifyPermissionError(showNotification, error);
        // Fallback to empty array if API fails
        setCounterparties([]);
      }
    };

    loadCounterparties();
  }, []);

  // adoptServerModifiedDates (after a 409): preserved edits move onto the fresh
  // rows' MODIFIED_DATE, so saving again is the user's deliberate re-apply.
  // Otherwise (the 30s refresh, a reload after a save) an edited row keeps the
  // MODIFIED_DATE its edits were based on, so a refresh never quietly turns
  // them into an overwrite of someone else's save.
  const loadEntries = useCallback(async (preserveLocalChanges = false, silent = false, consumedPlaceholderId = null, { adoptServerModifiedDates = false } = {}) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }
      // Error cleared - using notification system

      const response = await fetchEntries();
      const formattedData = response.data.map(entry => {
        // Convert all null values to empty strings using 1823-core utility
        const formattedEntry = nullToEmpty(entry);
        // Add specific formatting for date and boolean fields
        formattedEntry.DATE = formatDate(formattedEntry.DATE);
        formattedEntry.ILLIQUID = formattedEntry.ILLIQUID === true ? 'Y' : formattedEntry.ILLIQUID === false ? 'N' : '';
        formattedEntry.FLOATER = formattedEntry.FLOATER === true ? 'Y' : formattedEntry.FLOATER === false ? 'N' : '';
        formattedEntry.OVERRIDE = formattedEntry.OVERRIDE === true ? 'Y' : formattedEntry.OVERRIDE === false ? 'N' : '';
        return formattedEntry;
      });

      if (preserveLocalChanges) {
        // Placeholders whose save did land (their id is in the fresh data):
        // drop the local copy rather than show the entry twice.
        const freshIds = new Set(formattedData.map(row => row.BLOTTER_ID));
        const landed = new Set(
          Object.keys(newRowIdsRef.current).filter(placeholder => freshIds.has(newRowIdsRef.current[placeholder]))
        );
        landed.forEach(placeholder => { delete newRowIdsRef.current[placeholder]; });
        if (consumedPlaceholderId) {
          delete newRowIdsRef.current[consumedPlaceholderId];
        }
        // Capture current state values and calculate final values
        setUpdatedRows(currentUpdatedRows => {
          setRowData(currentRowData => {
            // If preserving local changes, merge them back into the fresh data
            let finalData = formattedData;

            if (Object.keys(currentUpdatedRows).length > 0 || currentRowData.some(row => row.BLOTTER_ID.startsWith('new_row_'))) {
              // First, apply unsaved changes to existing server data
              const updatedServerData = formattedData.map(row => {
                const rowChanges = currentUpdatedRows[row.BLOTTER_ID];
                if (rowChanges) {
                  // Apply unsaved changes to this row. Skip UI-only meta keys
                  // (e.g. _isNewRow) so they don't leak onto the row object and
                  // ride the save payload to the backend.
                  const modifiedRow = { ...row };
                  Object.keys(rowChanges).forEach(field => {
                    // The writer stamps MODIFIED_DATE; a tracked change to it is never applied.
                    if (field.startsWith('_') || field === MODIFIED_DATE_FIELD) return;
                    modifiedRow[field] = rowChanges[field].newValue;
                  });
                  if (!adoptServerModifiedDates) {
                    const baseRow = currentRowData.find(existing => existing.BLOTTER_ID === row.BLOTTER_ID);
                    if (baseRow) {
                      modifiedRow[MODIFIED_DATE_FIELD] = baseRow[MODIFIED_DATE_FIELD];
                    }
                  }
                  return modifiedRow;
                }
                return row;
              });

              // Collect all new rows that need to be preserved
              const preservedNewRows = [];
              currentRowData.forEach(existingRow => {
                if (existingRow.BLOTTER_ID.startsWith('new_row_')) {
                  // Drop the placeholder that was just persisted via the modal.
                  // The backend rewrote its ID to a real UUID, so it won't
                  // match any row in `formattedData` by BLOTTER_ID.
                  if (existingRow.BLOTTER_ID === consumedPlaceholderId || landed.has(existingRow.BLOTTER_ID)) {
                    return;
                  }
                  // Check if this new row is already in the fresh data
                  const existsInFreshData = updatedServerData.some(row => row.BLOTTER_ID === existingRow.BLOTTER_ID);
                  if (!existsInFreshData) {
                    preservedNewRows.push(existingRow);
                  }
                }
              });

              // Put new rows at the top, followed by server data
              finalData = [...preservedNewRows, ...updatedServerData];
            }

            return finalData;
          });

          // Trigger analytics update when data is refreshed with preserved changes
          setAnalyticsRefreshTrigger(prev => prev + 1);

          // Also drop the consumed placeholder from updatedRows tracking so
          // `isRowUpdated` no longer flags anything for it.
          const stale = [consumedPlaceholderId, ...landed].filter(id => id && currentUpdatedRows[id]);
          let next = currentUpdatedRows;
          if (stale.length > 0) {
            next = { ...currentUpdatedRows };
            stale.forEach(id => { delete next[id]; });
          }
          if (adoptServerModifiedDates) {
            // The edits now sit on the fresh rows: a later revert compares
            // with the fresh values.
            const freshById = new Map(formattedData.map(row => [row.BLOTTER_ID, row]));
            next = Object.fromEntries(Object.entries(next).map(([id, fields]) => {
              const fresh = freshById.get(id);
              if (!fresh) return [id, fields];
              return [id, Object.fromEntries(Object.entries(fields).map(([field, change]) => (
                field.startsWith('_') || !(field in fresh) ? [field, change] : [field, { ...change, oldValue: fresh[field] }]
              )))];
            }));
          }
          return next;
        });
      } else {
        setRowData(formattedData);
        setUpdatedRows({});
        // Trigger analytics update when data is reloaded
        setAnalyticsRefreshTrigger(prev => prev + 1);
      }
      return formattedData;
    } catch (error) {
      console.error("Error fetching entries:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to load entries. Please try again.", "error");
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [showNotification]);

  // After the modal's own 409, reload (other edits keep their tokens) and
  // hand the modal its row's fresh MODIFIED_DATE, so saving again re-applies
  // the form's input. Null when the row is gone (a re-create).
  const reloadAfterModalWriteError = useCallback(async (blotterId) => {
    const fresh = await loadEntries(true, true);
    return (fresh || []).find(entry => entry.BLOTTER_ID === blotterId) || null;
  }, [loadEntries]);

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount to avoid infinite loop

  // Background refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Silently refresh data while preserving local changes
      loadEntries(true, true); // preserveLocalChanges=true, silent=true
    }, 30000); // 30 seconds

    return () => clearInterval(interval); // Cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only set up once on mount

  const handleEditEntry = useCallback((entryData) => {
    setEditingEntry(entryData);
    setModalOpen(true);
  }, []);

  const handleViewHistory = useCallback((entryData) => {
    setSelectedEntryForHistory(entryData);
    setHistoryModalOpen(true);
  }, []);

  const handleDeleteEntry = useCallback((entryData) => {
    if (entryData.BLOTTER_ID.startsWith('new_row_')) {
      // If it's a new row that hasn't been saved yet, just remove it from the local state
      setRowData(prev => prev.filter(row => row !== entryData));
      // Also remove from updated rows tracking
      setUpdatedRows(prev => {
        const updated = { ...prev };
        delete updated[entryData.BLOTTER_ID];
        return updated;
      });
      // Trigger analytics update when rows are deleted
      setAnalyticsRefreshTrigger(prev => prev + 1);
      return;
    }

    setEntryToDelete(entryData);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDeleteEntry = useCallback(async () => {
    if (!entryToDelete) return;

    try {
      // The row as loaded: its MODIFIED_DATE is the token the writer checks
      // (the writer stamps MODIFIED_BY / MODIFIED_DATE on the tombstone).
      const formattedEntry = {
        ...formatEntryData({ ...entryToDelete, MODIFIED_BY: userId }),
        [MODIFIED_DATE_FIELD]: loadedModifiedDate(entryToDelete),
      };
      await deleteEntry(formattedEntry);

      // Refresh the data after successful deletion, preserve any unsaved changes
      await loadEntries(true);
      // Trigger analytics update after successful deletion
      setAnalyticsRefreshTrigger(prev => prev + 1);
      showNotification('Entry deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting entry:', error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification(getWriteErrorMessage(error, 'Failed to delete entry'), 'error');
      }
      if (shouldReloadAfterWriteError(error)) {
        // The conflict is about the deleted row: show it as saved now (its
        // unsaved edits are dropped; it was being deleted), so deleting it
        // again is a deliberate delete of the latest row. Other unsaved edits
        // keep the MODIFIED_DATE they were based on.
        const deletedId = entryToDelete.BLOTTER_ID;
        setUpdatedRows(prev => {
          if (!prev[deletedId]) return prev;
          const next = { ...prev };
          delete next[deletedId];
          return next;
        });
        await loadEntries(true, true);
      }
    } finally {
      // Close the dialog and reset state
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  }, [entryToDelete, loadEntries, showNotification, userId]);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setEntryToDelete(null);
  }, []);

  const handleCloseValidationErrorModal = useCallback(() => {
    setValidationErrorModalOpen(false);
    setValidationErrors([]);
  }, []);

  const handleDuplicateEntry = useCallback((entryData) => {
    const nextRowCount = newRowCount + 1;
    setNewRowCount(nextRowCount);

    // Create a new entry with only specific fields copied
    const duplicatedEntry = {
      BLOTTER_ID: `new_row_${nextRowCount}`,
      CUSIP: entryData.CUSIP,
      DATE: entryData.DATE,
      COUNTERPARTY: entryData.COUNTERPARTY,
      CONNECTED_ENTITY: entryData.CONNECTED_ENTITY,
      DESCRIPTION: entryData.DESCRIPTION,
      FLOATER: entryData.FLOATER,
      ILLIQUID: entryData.ILLIQUID,
      OVERRIDE: entryData.OVERRIDE,
      SEC_OWNER: entryData.SEC_OWNER,
      SEC_OWNER_TEAM: entryData.SEC_OWNER_TEAM,
      TYPE: entryData.TYPE,
      MODIFIED_BY: userId,
      // A new key: the writer accepts a null token only for a never-saved row.
      MODIFIED_DATE: null
    };

    // Initialize all other fields as empty strings
    const allFields = [
      "DIRECTION", "CUSIP", "CURRENCY", "ILLIQUID", "FLOATER", "DESCRIPTION",
      "ORIGINAL_FACE", "CURRENT_FACE", "PRICE", "EST_PROCEEDS",
      "MARKET_SPREAD", "TREAS_SPREAD", "BASE_YIELD", "WAL",
      "DURATION", "OVERRIDE", "RATING", "CURR_CREDIT_SUPPORT",
      "SSFA_DETACHMENT_POINT", "THICKNESS", "RISK_RETENTION",
      "ACCOUNT", "NOTES"
    ];

    allFields.forEach(field => {
      if (!duplicatedEntry.hasOwnProperty(field)) {
        duplicatedEntry[field] = "";
      }
    });

    // Add the duplicated row at the top of the data
    setRowData(prev => [duplicatedEntry, ...prev]);

    // Track the new row as modified so it can be saved
    setUpdatedRows(prev => ({
      ...prev,
      [`new_row_${nextRowCount}`]: {
        created: {
          oldValue: null,
          newValue: 'new_row',
          timestamp: new Date().toISOString()
        }
      }
    }));

    // Trigger analytics update when new rows are added
    setAnalyticsRefreshTrigger(prev => prev + 1);

    showNotification('Entry duplicated successfully with selected fields', 'success');
  }, [newRowCount, userId, showNotification]);

  const handleImportBlotter = useCallback(() => {
    importFileInputRef.current?.click();
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    const link = document.createElement('a');
    link.href = '/sample_blotter_template.csv';
    link.download = 'blotter_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleImportFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const parsedData = await parseFileData(file);

      if (!parsedData || parsedData.length === 0) {
        showNotification('File must contain at least one data row', 'error');
        return;
      }

      const uploadedData = [];

      parsedData.forEach((row) => {
        if (!row || Object.keys(row).length === 0) return;

        // Normalize header names to database column names using 1823-core utilities
        const entry = normalizeRowKeys(row, entryColumnNormalizer);

        // Process numeric, date, boolean, and calculated fields
        processEntryRow(entry, userId);

        // Format the entry data (calculations already done above)
        const formattedEntry = formatEntryData(entry);
        uploadedData.push(formattedEntry);
      });

      // Validate dropdown values before sending to API
      const dropdownErrors = [];
      uploadedData.forEach((row, idx) => {
        const err = validateDropdownValues(row, idx);
        if (err) {
          const cusip = row.CUSIP || `Row ${idx + 1}`;
          dropdownErrors.push(`Row ${idx + 1} (${cusip}): ${err.field} value "${err.value}" is not valid. ${err.message}`);
        }
      });

      if (dropdownErrors.length > 0) {
        showNotification(
          `Upload validation failed:\n${dropdownErrors.join('\n')}`,
          'error'
        );
        return;
      }

      // Send to upload blotter API
      await importBlotter(uploadedData);

      // Refresh the data to show the uploaded entries, preserve any unsaved changes
      await loadEntries(true);

      // Trigger analytics update after import
      setAnalyticsRefreshTrigger(prev => prev + 1);

      showNotification(`Successfully uploaded ${uploadedData.length} entries`, 'success');

    } catch (error) {
      console.error('Error uploading file:', error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification(
          error?.response
            ? getWriteErrorMessage(error, 'Error importing the blotter. Please try again.')
            : `Error uploading file: ${error.message}`,
          'error'
        );
      }
      if (shouldReloadAfterWriteError(error)) {
        await loadEntries(true, true);
      }
    }

    // Reset file input
    event.target.value = '';
  }, [userId, showNotification, loadEntries]);

  // Function to determine cell classes based on update status.
  // Read tracking from a ref so columnDefs/defaultColDef stay stable across
  // sequential edits — rebuilding them mid-edit destroys AG Grid's editor.
  const getCellClass = useCallback((params) => {
    const { data, colDef } = params;
    const field = colDef.field;

    // Skip non-data columns
    if (!data || !field || ['actions', 'select', 'edit'].includes(field)) {
      return '';
    }

    const blotterId = data.BLOTTER_ID;
    const isNewRow = blotterId && blotterId.startsWith('new_row_');
    const tracking = updatedRowsRef.current[blotterId];
    const isUpdated = tracking && tracking[field];

    if (isNewRow) {
      return 'ag-cell-updated-new-row';
    } else if (isUpdated) {
      return 'ag-cell-updated';
    }

    return '';
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "Blotter ID",
      field: "BLOTTER_ID",
      editable: false,
      getQuickFilterText: '',
      hide: true
    },
    {
      headerName: "Direction",
      headerTooltip: "Whether this is a Buy or Sell transaction",
      field: "DIRECTION",
      pinned: 'left',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.directions
      }
    },
    {
      headerName: "Trade Date",
      headerTooltip: "The date when the trade was executed",
      field: "DATE",
      valueFormatter: params => formatDate(params.value),
      valueParser: params => formatDate(params.newValue),
      pinned: 'left',
      cellClass: 'tradeDate',
      cellEditor: 'agDateCellEditor',
      cellEditorParams: {
        browserDatePicker: true
      },
      filter: 'agDateColumnFilter',
      filterParams: {
        browserDatePicker: true,
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const cellDate = new Date(cellValue);
          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        }
      }
    },
    {
      headerName: "CUSIP",
      headerTooltip: "Committee on Uniform Securities Identification Procedures number - unique identifier for the security",
      field: "CUSIP",
      pinned: 'left'
    },
    {
      headerName: "Currency",
      headerTooltip: "The currency denomination for this security",
      field: "CURRENCY",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.currencies
      }
    },
    {
      headerName: "Security Owner",
      headerTooltip: "The person or entity responsible for managing this security",
      field: "SEC_OWNER",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: params => ({
        values: getOwnerFieldOptions('SEC_OWNER', params.value)
      })
    },
    {
      headerName: "Security Owner Team",
      headerTooltip: "The team responsible for managing this security",
      field: "SEC_OWNER_TEAM",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: params => ({
        values: getOwnerFieldOptions('SEC_OWNER_TEAM', params.value)
      })
    },
    {
      headerName: "Type",
      headerTooltip: "The type/category of security (e.g., Corporate Bond, Municipal, etc.)",
      field: "TYPE",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.securityTypes
      }
    },
    {
      headerName: "Counterparty",
      headerTooltip: "The other party involved in the transaction",
      field: "COUNTERPARTY",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: counterparties
      }
    },
    {
      headerName: "Connected Entity",
      headerTooltip: "Any related entity or subsidiary connected to the counterparty",
      field: "CONNECTED_ENTITY",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.connectedEntities
      }
    },
    {
      headerName: "Illiquid (Y/N)",
      headerTooltip: "Indicates if the security is private or hard to trade (at discretion of investment team)",
      field: "ILLIQUID",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.booleanOptions
      },
      getQuickFilterText: ''
    },
    {
      headerName: "Floater (Y/N)",
      headerTooltip: "Indicates if this is a floating rate security (fixed-to-float should be N)",
      field: "FLOATER",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.booleanOptions
      },
      getQuickFilterText: ''
    },
    {
      headerName: "Description",
      headerTooltip: "Additional descriptive information about the security",
      field: "DESCRIPTION"
    },
    {
      headerName: "Original Face",
      headerTooltip: "The original principal amount of the security when first issued",
      field: "ORIGINAL_FACE",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Current Face",
      headerTooltip: "The current outstanding principal amount of the security",
      field: "CURRENT_FACE",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Price",
      headerTooltip: "The price at which the security was traded",
      field: "PRICE",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Est Proceeds",
      headerTooltip: "Estimated proceeds from the trade (calculated as Price × Current Face ÷ 100)",
      field: "EST_PROCEEDS",
      type: 'numericColumn',
      cellClass: getCellClass,
      editable: false,
      valueGetter: params => {
        const price = params.data.PRICE;
        const currentFace = params.data.CURRENT_FACE;
        const direction = params.data.DIRECTION;
        if (isValueNull(price) || isValueNull(currentFace) || !direction) return null;

        const multiplier = direction.toUpperCase() === 'BUY' ? 1 : -1;
        return (price * currentFace / 100) * multiplier;
      },
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      }
    },
    {
      headerName: "Market Convention Spread",
      headerTooltip: "The spread over the benchmark using market conventions",
      field: "MARKET_SPREAD",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Treasury Equivalent Spread",
      headerTooltip: "The spread over Treasury securities with equivalent duration",
      field: "TREAS_SPREAD",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Base Case Yield",
      headerTooltip: "The expected yield under base case assumptions",
      field: "BASE_YIELD",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue, { percent: true }),
    },
    {
      headerName: "WAL",
      headerTooltip: "Weighted Average Life - average time until principal is repaid",
      field: "WAL",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Duration",
      headerTooltip: "Measure of price sensitivity to interest rate changes",
      field: "DURATION",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Override (Y/N)",
      headerTooltip: "Indicates if this entry has pricing or valuation overrides (used for Surveillance process)",
      field: "OVERRIDE",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.booleanOptions
      },
      getQuickFilterText: ''
    },
    {
      headerName: "Rating at Trade",
      headerTooltip: "The credit rating of the security at the time of trade",
      field: "RATING",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.ratings
      }
    },
    {
      headerName: "Current Credit Support",
      headerTooltip: "Current level of credit enhancement or subordination",
      field: "CURR_CREDIT_SUPPORT",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "SSFA Detachment Point",
      headerTooltip: "Simplified Supervisory Formula Approach detachment point for capital calculation",
      field: "SSFA_DETACHMENT_POINT",
      type: 'numericColumn',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      },
      valueParser: params => parseNumericCellValue(params.newValue),
    },
    {
      headerName: "Thickness",
      headerTooltip: "The thickness of the tranche (calculated as SSFA Detachment Point - Current Credit Support)",
      field: "THICKNESS",
      type: 'numericColumn',
      cellClass: getCellClass,
      editable: false,
      valueGetter: params => {
        const currCreditSupport = params.data.CURR_CREDIT_SUPPORT;
        const ssfaDetachmentPoint = params.data.SSFA_DETACHMENT_POINT;
        if (isValueNull(currCreditSupport) || isValueNull(ssfaDetachmentPoint)) return '';
        return ssfaDetachmentPoint - currCreditSupport;
      },
      valueFormatter: params => {
        if (isValueNull(params.value)) return '';
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
      }
    },
    {
      headerName: "Risk Retention",
      headerTooltip: "Risk retention type for the securitisation structure",
      field: "RISK_RETENTION",
      cellClass: getCellClass,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.riskRetentionOptions
      },
      valueGetter: (params) => {
        const val = params.data?.RISK_RETENTION;
        return isValueNull(val) ? 'N/A' : val;
      },
      valueSetter: (params) => {
        const allowedValues = config.riskRetentionOptions || [];
        const newValue = params.newValue;

        // Required field: disallow blank/null and anything not in the options list.
        if (!newValue || !allowedValues.includes(newValue)) {
          return false;
        }

        params.data.RISK_RETENTION = newValue;
        return true;
      }
    },
    {
      headerName: "Account",
      headerTooltip: "The account or entity that owns this position",
      field: "ACCOUNT",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.accounts
      }
    },
    {
      headerName: "Notes",
      headerTooltip: "Additional comments or notes about this entry",
      field: "NOTES",
      minWidth: 200,
      flex: 2,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 1000,
        rows: 3
      }
    },
    {
      headerName: "Modified By",
      headerTooltip: "User who last modified this entry",
      field: "MODIFIED_BY",
      editable: false
    },
    {
      headerName: "Modified Date",
      headerTooltip: "Date and time when this entry was last modified",
      field: "MODIFIED_DATE",
      editable: false,
      valueFormatter: params => {
        if (!params.value) return '';
        try {
          const date = new Date(params.value);
          return date.toLocaleString();
        } catch (error) {
          return params.value;
        }
      },
      comparator: (valueA, valueB) => {
        if (!valueA && !valueB) return 0;
        if (!valueA) return -1;
        if (!valueB) return 1;

        try {
          const dateA = new Date(valueA).getTime();
          const dateB = new Date(valueB).getTime();

          if (isNaN(dateA) && isNaN(dateB)) return 0;
          if (isNaN(dateA)) return -1;
          if (isNaN(dateB)) return 1;

          return dateA - dateB;
        } catch (error) {
          return 0;
        }
      }
    },
  ], [counterparties, getCellClass]);

  // Create a mapping from field names to header names for better validation error display
  const fieldToHeaderMap = useMemo(() => {
    const mapping = {};
    columnDefs.forEach(colDef => {
      if (colDef.field && colDef.headerName) {
        mapping[colDef.field] = colDef.headerName;
      }
    });
    return mapping;
  }, [columnDefs]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    editable: !isLoading,
    cellClass: getCellClass,
    cellStyle: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider
    },
    menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
    lockPinned: false
  }), [theme, isLoading, getCellClass]);

  const handleBlankRow = useCallback(() => {
    const nextRowCount = newRowCount + 1;
    setNewRowCount(nextRowCount);

    const newRow = {};
    columnDefs.forEach(def => {
      const col = def.field;
      if (col === "BLOTTER_ID") {
        newRow[col] = `new_row_${nextRowCount}`;
      } else if (col === "MODIFIED_BY") {
        newRow[col] = userId;
      } else if (col === "MODIFIED_DATE") {
        // A new key: the writer accepts a null token only for a never-saved row.
        newRow[col] = null;
      } else if (col === "CURRENCY") {
        newRow[col] = "USD"; // Default currency to USD for new rows
      } else if (!["select", "edit"].includes(col)) {
        newRow[col] = "";
      }
    });

    // Track new row as an update
    setUpdatedRows(prev => ({
      ...prev,
      [newRow.BLOTTER_ID]: {
        _isNewRow: {
          oldValue: null,
          newValue: true,
          timestamp: new Date().toISOString()
        }
      }
    }));

    setRowData(prev => [newRow, ...prev]);

    // Trigger analytics update when new rows are added
    setAnalyticsRefreshTrigger(prev => prev + 1);
  }, [columnDefs, newRowCount, userId]);


  // Extract unique currencies from the grid data
  const availableCurrencies = useMemo(() => {
    if (!gridRef.current || !gridRef.current.api) return ['USD'];

    const currencies = new Set(['USD']); // Always include USD as default
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      const currency = node.data.CURRENCY;
      if (currency && currency.trim() !== '') {
        currencies.add(currency);
      }
    });
    return Array.from(currencies).sort();
  }, []);

  // Update selected currency if it's no longer available
  useEffect(() => {
    if (!availableCurrencies.includes(analyticsCurrency)) {
      setAnalyticsCurrency('USD');
    }
  }, [availableCurrencies, analyticsCurrency]);

  // Calculate analytics for numeric fields
  const calculateAnalytics = useCallback(() => {
    if (!gridRef.current || !gridRef.current.api) return {};

    // Get filtered data from the grid
    const filteredData = [];
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      // Filter by selected currency
      if (node.data.CURRENCY === analyticsCurrency) {
        filteredData.push(node.data);
      }
    });

    const numericFields = [
      'ORIGINAL_FACE', 'CURRENT_FACE', 'PRICE', 'EST_PROCEEDS',
      'MARKET_SPREAD', 'TREAS_SPREAD', 'BASE_YIELD', 'WAL',
      'DURATION', 'CURR_CREDIT_SUPPORT', 'SSFA_DETACHMENT_POINT', 'THICKNESS'
    ];

    const analytics = {};

    numericFields.forEach(field => {
      const values = filteredData
        .map(row => row[field])
        .filter(val => val !== null && val !== undefined && val !== "" && !isNaN(val) && isFinite(val));

      if (values.length === 0) {
        analytics[field] = null;
        return;
      }

      switch (analyticsType) {
        case 'sum':
          analytics[field] = values.reduce((sum, val) => sum + val, 0);
          break;
        case 'mean':
          analytics[field] = values.reduce((sum, val) => sum + val, 0) / values.length;
          break;
        case 'median':
          const sorted = [...values].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          analytics[field] = sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];
          break;
        case 'mode':
          const frequency = {};
          values.forEach(val => {
            frequency[val] = (frequency[val] || 0) + 1;
          });
          const maxFreq = Math.max(...Object.values(frequency));
          const modes = Object.keys(frequency).filter(key => frequency[key] === maxFreq);
          analytics[field] = modes.length === 1 ? Number(modes[0]) : null;
          break;
        case 'min':
          analytics[field] = Math.min(...values);
          break;
        case 'max':
          analytics[field] = Math.max(...values);
          break;
        case 'count':
          analytics[field] = values.length;
          break;
        default:
          analytics[field] = null;
      }
    });

    return analytics;
  }, [analyticsType, analyticsCurrency]);

  // Memoized analytics data that updates when analyticsType, filters, or data changes
  const analyticsData = useMemo(() => {
    return calculateAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateAnalytics, quickFilterText, rowData, filtersChanged, analyticsRefreshTrigger, analyticsType]);

  // Format analytics values for display
  const formatAnalyticsValue = (field, value) => {
    if (value === null || value === undefined) return 'N/A';

    if (field === 'BASE_YIELD') {
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      }).format(value);
    }

    if (field === 'WAL') {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(value);
    }

    if (analyticsType === 'count') {
      return value.toString();
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const onCellValueChanged = useCallback((params) => {
    const { data, colDef, newValue, api } = params;
    const now = new Date().toISOString();
    // AG Grid has already written newValue into `data`; its `oldValue` is
    // the cell's value before this edit.
    const previousFieldValue = params.oldValue;
    const previousModifiedBy = data.MODIFIED_BY;

    // Update MODIFIED_BY directly on the AG Grid row data so the grid displays
    // it immediately (React state updates alone don't reliably refresh
    // non-edited cells because AG Grid's internal model already committed the
    // cell edit before this callback fires). MODIFIED_DATE is left as loaded:
    // it is the token the save sends back, and the writer stamps the new one.
    data.MODIFIED_BY = userId;

    const nextTracking = (prev) => {
      const rowTracking = { ...(prev[data.BLOTTER_ID] || {}) };
      const isNewRow = String(data.BLOTTER_ID).startsWith('new_row_');
      // The value the row was loaded with: the first edit's oldValue
      // (undefined when the caller did not say, so no revert is detected).
      const loadedValue = rowTracking[colDef.field] ? rowTracking[colDef.field].oldValue : previousFieldValue;
      if (!isNewRow && loadedValue !== undefined && sameCellValue(newValue, loadedValue)) {
        // Set back to the loaded value: not an edit any more.
        delete rowTracking[colDef.field];
      } else {
        rowTracking[colDef.field] = { oldValue: loadedValue, newValue: newValue, timestamp: now };
      }
      const userFields = Object.keys(rowTracking).filter(field => field !== 'MODIFIED_BY');
      if (!isNewRow && userFields.length === 0) {
        const next = { ...prev };
        delete next[data.BLOTTER_ID];
        return next;
      }
      rowTracking.MODIFIED_BY = rowTracking.MODIFIED_BY || { oldValue: previousModifiedBy, newValue: userId, timestamp: now };
      return { ...prev, [data.BLOTTER_ID]: rowTracking };
    };

    // During paste, AG Grid mutates row objects in place. Cloning React state
    // (and rebuilding columnDefs via updatedRows) mid-paste interrupts the
    // remaining clipboard cells, so batch tracking until onPasteEnd.
    if (isPastingRef.current) {
      if (pendingUpdatedRowsRef.current === null) {
        pendingUpdatedRowsRef.current = { ...updatedRowsRef.current };
      }
      pendingUpdatedRowsRef.current = nextTracking(pendingUpdatedRowsRef.current);
      return;
    }

    // AG Grid already mutated `data` in place. Keep that object identity so
    // Tab/Shift-Tab/typed edits are not interrupted by a rowData clone, and
    // keep getCellClass on a ref so columnDefs stay stable. Refresh the
    // edited cell after updating the tracking ref so tan classes apply.
    const next = nextTracking(updatedRowsRef.current);
    updatedRowsRef.current = next;
    setUpdatedRows(next);

    if (api?.refreshCells) {
      const rowNode = api.getRowNode?.(data.BLOTTER_ID);
      api.refreshCells({
        rowNodes: rowNode ? [rowNode] : undefined,
        columns: [colDef.field, 'MODIFIED_BY'],
        force: true
      });
    }

    // Trigger analytics update when cells change
    setAnalyticsRefreshTrigger(prev => prev + 1);
  }, [userId]);

  const onPasteStart = useCallback(() => {
    isPastingRef.current = true;
    pendingUpdatedRowsRef.current = null;
  }, []);

  const onPasteEnd = useCallback(() => {
    isPastingRef.current = false;
    const pending = pendingUpdatedRowsRef.current;
    pendingUpdatedRowsRef.current = null;
    if (pending) {
      updatedRowsRef.current = pending;
      setUpdatedRows(pending);
      // Snapshot in-place AG Grid mutations into React state after paste completes
      setRowData(prev => prev.map(row => ({ ...row })));
    }
    showNotification('Data pasted successfully', 'success');
    // Trigger analytics update after paste operations
    setAnalyticsRefreshTrigger(prev => prev + 1);
  }, [showNotification]);

  // Handle column filter changes to update analytics
  const onFilterChanged = useCallback(() => {
    setFiltersChanged(prev => prev + 1);
  }, []);

  // Toast handler for Ctrl+C — PamGrid does the actual copy and calls this
  // with { scope: 'range' | 'cell' }. Ctrl+V is handled by PamGrid natively.
  const handleCopy = useCallback(({ scope }) => {
    showNotification(
      scope === 'range' ? 'Selected cells copied to clipboard' : 'Cell copied to clipboard',
      'success'
    );
  }, [showNotification]);

  // Helper functions for working with updated rows dictionary
  const isRowUpdated = useCallback((blotterId) => {
    return Object.prototype.hasOwnProperty.call(updatedRows, blotterId);
  }, [updatedRows]);

  const validateSave = useCallback(() => {
    // Only validate rows that will be saved (changed rows and new rows)
    const rowsToSave = rowData.filter(row =>
      isRowUpdated(row.BLOTTER_ID) || row.BLOTTER_ID.startsWith('new_row_')
    );
    return validateEntryList(rowsToSave);
  }, [rowData, isRowUpdated]);

  const handleClearFilters = useCallback(() => {
    // Clear the quick filter text
    setQuickFilterText('');

    // Clear all column filters using AG-Grid API
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setFilterModel(null);
      gridRef.current.api.onFilterChanged();
      showNotification('All filters cleared', 'success');
    }
  }, [showNotification]);

  const handleSave = useCallback(async () => {
    // Get only rows that have changes or are new
    const changedRows = rowData.filter(row =>
      isRowUpdated(row.BLOTTER_ID) || row.BLOTTER_ID.startsWith('new_row_')
    );

    if (changedRows.length === 0) {
      showNotification('No changes to save', 'info');
      return;
    }

    const validationErrorsList = validateSave();

    if (validationErrorsList.length !== 0) {
      console.error("Validation Error");
      setValidationErrors(validationErrorsList);
      setValidationErrorModalOpen(true);
    } else {
      try {
        setIsLoading(true); // Disable grid during save
        // Each row carries the MODIFIED_DATE it was loaded with; a new row
        // sends its stable BLOTTER_ID and a null token.
        const dataToSave = changedRows.map(entry => {
          const row = { ...formatEntryData(entry), [MODIFIED_DATE_FIELD]: loadedModifiedDate(entry) };
          if (isPlaceholderEntryId(entry.BLOTTER_ID)) {
            row.BLOTTER_ID = reserveEntryId(entry.BLOTTER_ID);
            row[MODIFIED_DATE_FIELD] = null;
          }
          return row;
        });


        await saveEntries(dataToSave);
        showNotification(`Successfully saved ${dataToSave.length} changed entries`, 'success');

        // Clear tracking only for saved rows, keep unsaved changes
        setUpdatedRows(prev => {
          const remaining = { ...prev };
          changedRows.forEach(row => {
            delete remaining[row.BLOTTER_ID];
          });
          return remaining;
        });

        // Remove new rows from local storage since they're now saved
        setRowData(prev => prev.filter(row => !row.BLOTTER_ID.startsWith('new_row_')));
        changedRows.forEach(row => { delete newRowIdsRef.current[row.BLOTTER_ID]; });

        // Reset new row counter since all new rows have been saved
        setNewRowCount(0);

        await loadEntries(true); // Refresh data after save and preserve unsaved changes
      } catch (error) {
        console.error("Error saving:", error);
        if (!notifyPermissionError(showNotification, error)) {
          // The backend's own text (conflict, bad request, outcome unknown).
          showNotification(getWriteErrorMessage(error, "Failed to save entries. Please try again."), "error");
        }
        setIsLoading(false); // Re-enable grid if there's an error
        if (shouldReloadAfterWriteError(error)) {
          // Someone else saved (or the save may have landed): reload, keeping
          // every unsaved edit and new row on screen to re-apply.
          await loadEntries(true, true, null, { adoptServerModifiedDates: isVersionConflict(error) });
        }
      }
    }
  }, [rowData, loadEntries, validateSave, showNotification, isRowUpdated, reserveEntryId]);

  // PamGrid upload handler wrapper - receives file directly from PamGrid
  const handlePamGridUpload = useCallback(async (file) => {
    if (!file) return;

    try {
      const parsedData = await parseFileData(file);

      if (!parsedData || parsedData.length === 0) {
        showNotification('File must contain at least one data row', 'error');
        return;
      }

      // Normalize headers and process rows via 1823-core utilities
      const normalizedRows = parsedData
        .filter(row => row && Object.keys(row).length > 0)
        .map(row => normalizeRowKeys(row, entryColumnNormalizer));

      // Validate dropdown values before processing
      const dropdownErrors = [];
      normalizedRows.forEach((row, idx) => {
        const err = validateDropdownValues(row, idx);
        if (err) {
          const cusip = row.CUSIP || `Row ${idx + 1}`;
          dropdownErrors.push(`Row ${idx + 1} (${cusip}): ${err.field} value "${err.value}" is not valid. ${err.message}`);
        }
      });

      if (dropdownErrors.length > 0) {
        showNotification(
          `Upload validation failed:\n${dropdownErrors.join('\n')}`,
          'error'
        );
        return;
      }

      // Use mergeRows with 'append' strategy — all uploaded rows become new rows
      const { mergedData } = mergeRows({
        uploadedRows: normalizedRows,
        existingRows: [],
        matchField: 'BLOTTER_ID',
        mergeStrategy: 'append',
        onNewRow: (row, i) => {
          const entry = { ...row };
          entry.BLOTTER_ID = `new_row_${newRowCount + i + 1}`;
          processEntryRow(entry, userId);
          return entry;
        },
      });

      if (mergedData.length > 0) {
        setNewRowCount(prev => prev + mergedData.length);
        setRowData(prev => [...mergedData, ...prev]);

        // Track all uploaded rows as modified
        const newUpdatedRows = {};
        mergedData.forEach(entry => {
          newUpdatedRows[entry.BLOTTER_ID] = {
            created: {
              oldValue: null,
              newValue: 'new_row',
              timestamp: new Date().toISOString()
            }
          };
        });
        setUpdatedRows(prev => ({ ...prev, ...newUpdatedRows }));
        setAnalyticsRefreshTrigger(prev => prev + 1);
        showNotification(`Successfully uploaded ${mergedData.length} entries`, 'success');
      } else {
        showNotification('No valid data found in the file', 'warning');
      }
    } catch (error) {
      console.error('Error processing upload:', error);
      showNotification('Error processing file. Please check the file format.', 'error');
    }
  }, [newRowCount, userId, showNotification]);

  // Custom toolbar buttons for Import Blotter and Template
  const customToolbarButtons = useMemo(() => {
    const buttons = [
      {
        label: 'Template',
        onClick: handleDownloadTemplate,
        tooltip: 'Download CSV template file with correct column headers and sample data',
        color: 'lightcyan'
      }
    ];

    // Add Import Blotter button only for authorized users
    if (userId === 'local' || userId === 'tyler.duic@1823.partners') {
      buttons.unshift({
        label: 'Import Blotter',
        onClick: handleImportBlotter,
        tooltip: 'Upload Excel (.xlsx, .xls) or CSV file to import blotter entries',
        color: 'mediumpurple'
      });
    }

    return buttons;
  }, [handleDownloadTemplate, handleImportBlotter, userId]);

  // Own the grid-config hook here (rather than letting GridConfigMenu
  // instantiate it internally) so we can wire gridConfigs.onGridReady to
  // PamGrid's onGridReady prop — without that bridge, a slow grid that
  // mounts AFTER the configs response would miss the default-layout
  // auto-apply. See useGridConfigs.js for the grid-ready race rationale.
  const gridConfigs = useGridConfigs(api, {
    app: 'blotter',
    gridId: 'entries',
    gridApiRef: gridRef,
  });

  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // Compose the existing filter handler with the grid-config stash writer so
  // unsaved layout tweaks survive a refresh without dropping the analytics
  // recompute trigger.
  const handleFilterChanged = useCallback((...args) => {
    onFilterChanged(...args);
    gridConfigs.onGridStateChanged(...args);
  }, [onFilterChanged, gridConfigs]);

  // Instructions for the grid
  const entryListInstructions = useMemo(() => (
    <>
      <strong>Copy/Paste Instructions:</strong> Select cells or ranges and use Ctrl+C to copy, Ctrl+V to paste.
      Works with Excel and other spreadsheet applications.
    </>
  ), []);

  // Legend items for cell highlighting
  const legendItems = useMemo(() => [
    { color: 'rgba(255, 165, 0, 0.2)', label: 'Modified Cell' },
    { color: 'rgba(255, 200, 100, 0.3)', label: 'New Row' }
  ], []);

  return (
    <Box>
      {/* Hidden file input for Import Blotter custom button */}
      <input
        type="file"
        ref={importFileInputRef}
        style={{ display: 'none' }}
        accept=".csv,.xlsx,.xls"
        onChange={handleImportFileUpload}
      />

      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        title="Blotter Entries"
        subtitle="Upload Excel (.xlsx, .xls) or CSV files to import blotter entries"
        showToolbar={true}
        showUpload={true}
        onUpload={handlePamGridUpload}
        showExport={true}
        exportConfig={{
          fileName: `blotter_entries_${new Date().toISOString().split('T')[0]}.xlsx`,
          sheetName: 'Blotter Entries',
        }}
        showAddRow={true}
        onAddRow={handleBlankRow}
        showSave={true}
        onSave={handleSave}
        changedRowCount={Object.keys(updatedRows).length}
        isSaving={isLoading}
        showClearFilters={true}
        onClearFilters={handleClearFilters}
        customToolbarButtons={customToolbarButtons}
        customToolbarComponents={gridConfigComponents}
        onGridReady={gridConfigs.onGridReady}
        agGridProps={gridConfigs.stashBindings}
        showQuickFilter={true}
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        height="calc(80vh - 300px)"
        rowHeight={32}
        showActions={true}
        actions={['history', 'edit', 'duplicate', 'delete']}
        onHistory={handleViewHistory}
        onEdit={handleEditEntry}
        onDuplicate={handleDuplicateEntry}
        onDelete={handleDeleteEntry}
        defaultColDef={defaultColDef}
        getRowId={(data) => data.BLOTTER_ID}
        isLoading={isLoading}
        onCellValueChanged={!isLoading ? onCellValueChanged : undefined}
        onCopy={handleCopy}
        allowPaste={!isLoading}
        onPasteStart={!isLoading ? onPasteStart : undefined}
        onPasteEnd={!isLoading ? onPasteEnd : undefined}
        onFilterChanged={handleFilterChanged}
        showInstructions={true}
        instructions={entryListInstructions}
        showLegend={true}
        legendItems={legendItems}
      />

      {/* Analytics Row */}
      {showAnalytics && (
        <Paper sx={{ mt: 2, p: 2, backgroundColor: theme.palette.background.paper }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" color="primary">
              Analytics ({analyticsType === 'count' && analyticsData.ORIGINAL_FACE !== null ?
                analyticsData.ORIGINAL_FACE :
                (analyticsData.ORIGINAL_FACE !== null ? 'Filtered' : 'No data')} entries)
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Analysis Type</InputLabel>
              <Select
                value={analyticsType}
                onChange={(e) => setAnalyticsType(e.target.value)}
                label="Analysis Type"
              >
                <MenuItem value="sum">Sum</MenuItem>
                <MenuItem value="mean">Mean</MenuItem>
                <MenuItem value="median">Median</MenuItem>
                <MenuItem value="mode">Mode</MenuItem>
                <MenuItem value="min">Minimum</MenuItem>
                <MenuItem value="max">Maximum</MenuItem>
                <MenuItem value="count">Count</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Currency</InputLabel>
              <Select
                value={analyticsCurrency}
                onChange={(e) => setAnalyticsCurrency(e.target.value)}
                label="Currency"
              >
                {availableCurrencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={2}>
            {[
              { field: 'ORIGINAL_FACE', label: 'Original Face' },
              { field: 'CURRENT_FACE', label: 'Current Face' },
              { field: 'PRICE', label: 'Price' },
              { field: 'EST_PROCEEDS', label: 'Est Proceeds' },
              { field: 'MARKET_SPREAD', label: 'Market Spread' },
              { field: 'TREAS_SPREAD', label: 'Treasury Spread' },
              { field: 'BASE_YIELD', label: 'Base Yield' },
              { field: 'WAL', label: 'WAL' },
              { field: 'DURATION', label: 'Duration' },
              { field: 'CURR_CREDIT_SUPPORT', label: 'Current Credit Support' },
              { field: 'SSFA_DETACHMENT_POINT', label: 'SSFA Detachment Point' },
              { field: 'THICKNESS', label: 'Thickness' }
            ].map(({ field, label }) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={field}>
                <Box sx={{
                  p: 1,
                  border: 1,
                  borderColor: theme.palette.divider,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[50]
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                    {formatAnalyticsValue(field, analyticsData[field])}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <CreateEntryModal
        open={modalOpen}
        onCreated={(consumedPlaceholderId) => loadEntries(true, false, consumedPlaceholderId)}
        onWriteConflict={reloadAfterModalWriteError}
        reserveEntryId={reserveEntryId}
        onClose={() => setModalOpen(false)}
        userId={userId}
        initialData={editingEntry}
      />

      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Entry History - ${selectedEntryForHistory?.CUSIP || selectedEntryForHistory?.DESCRIPTION || 'Entry'}`}
      >
        <EntryHistory entryId={selectedEntryForHistory?.BLOTTER_ID} showTitle={false} />
      </HistoryModal>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteEntry}
        title="Delete Entry"
        message={
          entryToDelete
            ? `Are you sure you want to delete "${entryToDelete.DESCRIPTION || entryToDelete.CUSIP || 'this entry'}"?\n\nThis action cannot be undone.`
            : "Are you sure you want to delete this entry?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />

      <ValidationErrorModal
        open={validationErrorModalOpen}
        onClose={handleCloseValidationErrorModal}
        errors={formatValidationErrors(validationErrors, fieldToHeaderMap)}
        title="Validation Errors Found"
        subtitle="Please fix the following validation errors before saving:"
      />
    </Box>
  );
};

export default EntryList;
